import type {
  CMSProductCardItem,
  CMSProductPage,
  CMSProductVariant,
} from '@/types/sanity'
import type { Cart, CartItem } from '@/types/shopify'
import type {
  ElevarTrackingItemData,
  GTMTrackingCartData,
  GTMTrackingItemData,
  ProductTrackingData,
} from '@/types/tracking'

export const waitForSelector = (
  parentNode: HTMLElement,
  targetSelector: string,
) => {
  return new Promise((resolve) => {
    const targetElement = parentNode.querySelector(targetSelector)
    if (targetElement) resolve(targetElement)
    const selectorObserver = new MutationObserver((_, observer) => {
      const targetElement = parentNode.querySelector(targetSelector)
      if (!targetElement) return
      observer.disconnect()
      resolve(targetElement)
    })
    selectorObserver.observe(parentNode, {
      attributes: true,
      childList: true,
      subtree: true,
    })
  })
}

export const transformCartToTrackingData = (cart: Cart) => {
  return {
    attributes: cart.attributes,
    cartTotal: cart.cost?.totalAmount?.amount,
    currencyCode: cart.cost?.totalAmount?.currencyCode,
    items: cart.lines?.map((line, index) =>
      mapCartLineItemToTrackingData(line, index + 1),
    ),
  }
}

const mapLineItemToDataLayerObject = (line: CartItem): GTMTrackingItemData => {
  return {
    id: line.merchandise.sku || line.merchandise.product.id,
    title: line.merchandise.product.title,
    product_title: line.merchandise.product.title,
    variant_title: line.merchandise.title,
    price: line.merchandise.price.amount,
    quantity: line.quantity,
  }
}

export const transformCartToDataLayerObject = (
  cart: Cart,
): GTMTrackingCartData => {
  return {
    attributes: cart.attributes.reduce(
      (acc, attr) => {
        acc[attr.key] = attr.value
        return acc
      },
      {} as Record<string, string>,
    ),
    total_price: Number(cart.cost.totalAmount.amount),
    currency: cart.cost.totalAmount.currencyCode,
    items: cart.lines.map(mapLineItemToDataLayerObject),
    item_count: cart.lines.reduce((acc, line) => acc + line.quantity, 0),
    items_subtotal_price: Number(cart.cost.subtotalAmount.amount),
  }
}

export const mapCartLineItemToTrackingData = (item: CartItem, position = 1) => {
  const { quantity } = item
  const {
    id,
    title,
    sku,
    price: { amount: priceAmount },
    compareAtPrice,
    image,
    product: { id: productGid, title: productTitle, vendor, productType },
  } = item.merchandise

  const productId = productGid.split('/').pop()!
  const variantId = id.split('/').pop()!

  const data: ElevarTrackingItemData = {
    id: sku || productId,
    name: productTitle,
    brand: vendor,
    category: productType,
    variant: title,
    price: priceAmount,
    quantity: String(quantity),
    productId,
    variantId,
    position,
  }

  if (position) data.position = position
  if (compareAtPrice) data.compareAtPrice = compareAtPrice.amount
  if (image) data.image = image.url

  return data
}

export const mapProductToProductTrackingData = (
  product: CMSProductPage,
  activeVariant: CMSProductVariant,
) => {
  const data: ElevarTrackingItemData = {
    id: product.id,
    title: product.details.title,
    brand: product.details.vendor,
    product_type: product.details.productType,
    variant: activeVariant.title,
    price: String(activeVariant.price * 100),
    tags: product.tags,
    variant_id: String(activeVariant.id),
    featured_image: { url: product.details.shopifyImageUrl },
    compareAtPrice: activeVariant.compareAtPrice
      ? String(activeVariant.compareAtPrice)
      : null,
    inventory: '1',
    handle: product.slug,
  }

  return data
}

export const updateElevarTrackingItemDataByActiveVariant = (
  raw: ElevarTrackingItemData,
  variant: CMSProductVariant,
) => ({
  ...raw,
  id: variant.sku,
  variant: variant.title,
  price: String(variant.price),
  variantId: String(variant.id),
  compareAtPrice: variant.compareAtPrice
    ? String(variant.compareAtPrice)
    : null,
})

export const mapProductCardToProductTrackingData = (
  card: CMSProductCardItem,
  position?: number,
) => {
  const {
    variants,
    activeVariant: { sku, id: activeVariantId },
    id,
    vendor,
    productType,
    title,
    slug,
  } = card || {}
  const activeVariant = variants.find((variant) => variant.sku === sku)!
  if (!activeVariant) {
    return {}
  }
  const data: ElevarTrackingItemData = {
    id: sku || String(id),
    name: title,
    brand: vendor,
    category: productType,
    variant: activeVariant.title,
    price: String(activeVariant.price),
    productId: String(id),
    variantId: String(activeVariantId),
    compareAtPrice: activeVariant.compareAtPrice
      ? String(activeVariant.compareAtPrice)
      : null,
    handle: slug,
  }

  if (position) data.position = position

  return data
}

export const transformProductCardToGTMItem = (
  card: CMSProductCardItem,
): GTMTrackingItemData => {
  const activeVariant = card.variants.find(
    (variant) => variant.sku === card.activeVariant.sku,
  )

  return {
    id: card.id.toString(),
    title: card.title,
    product_title: card.title,
    variant_title: activeVariant?.title,
    product_id: card.id.toString(),
    product_type: card.productType,
    featured_image: {
      url: card.productImage?.desktopImage?.asset?.url,
    },
    vendor: card.vendor,
    price: activeVariant?.price?.toString(),
    variant_id: card.activeVariant.id.toString(),
    sku: card.activeVariant.sku,
  }
}

export const mapProductCardToChordProductTrackingData = (
  card: CMSProductCardItem,
) => {
  const {
    variants,
    activeVariant: { sku },
  } = card
  const activeVariant = variants.find((variant) => variant.sku === sku)!
  if (!activeVariant) {
    return false
  }

  const variantList = variants.map((variant) => {
    return {
      sku: variant.sku,
      title: variant.title,
      price: variant.price,
    }
  })

  const data = {
    product: {
      details: {
        title: card.title,
        id: card.id,
      },
      slug: card.slug,
      variants: [variantList[0]],
    },
    variant: card.activeVariant.id,
  }

  return data
}

export const transformElevarTrackingItemDataToGTMTrackingItemData = (
  raw: ElevarTrackingItemData,
) =>
  ({
    id: raw.productId,
    title: raw.name,
    product_title: raw.title,
    variant_title: raw.variant,
    product_id: raw.productId,
    product_type: raw.category,
    featured_image: {
      url: raw.featured_image?.url,
    },
    vendor: raw.brand,
    price: (Number(raw.price) * 100).toString(),
    variant_id: raw.variantId,
    sku: raw.id,
  }) as GTMTrackingItemData

export const getDefaultProductViewData = (): ProductTrackingData | void => {
  const baseTrackingData = document.querySelector('#product-tracking-data')
    ?.textContent
  if (!baseTrackingData) return
  return JSON.parse(baseTrackingData)
}

export const getDefaultCollectionViewData = () => {
  const baseTrackingData = document.querySelector('#collection-tracking-data')
    ?.textContent
  if (!baseTrackingData) return []
  return JSON.parse(baseTrackingData)
}

export const getBuyingPanelAddToCartTrackingData = (
  activeVariant: CMSProductVariant,
) => {
  const productData = getDefaultProductViewData()
  if (!productData) throw new Error('Product data not found')
  const updatedProductData = updateElevarTrackingItemDataByActiveVariant(
    productData,
    activeVariant,
  )
  const transformedData =
    transformElevarTrackingItemDataToGTMTrackingItemData(updatedProductData)
  return {
    ...transformedData,
    quantity: 1,
  }
}

export const getCartPopupDialogItem = (item: any) => {
  return { ...item }
}

export const transformGTMItemToSimonItem = (item: GTMTrackingItemData) => {
  return {
    productId: item.id,
    variant: item.variant_id || item.variant_title,
    quantity: 1,
    productImageUrl: item.featured_image?.url,
    brand: item.vendor,
    category: item.product_type,
    productName: item.product_title,
    price: Number(item.price) / 100,
  }
}

export const transformCartItemToSimonItem = (item: CartItem) => {
  return {
    productId: item.merchandise.product.id,
    variant: item.merchandise.id,
    productImageUrl: item.merchandise.product.featuredImage?.url,
    brand: item.merchandise.product.vendor,
    category: item.merchandise.product.productType,
    productName: item.merchandise.product.title,
    price: Number(item.merchandise.price.amount),
  }
}

export const transformActiveVariantToGTMItem = (
  activeVariant: any,
  cart?: any,
) => {
  // If the product is not found in the active variant, we try to find it in the cart
  if (!activeVariant.product && cart?.lines) {
    activeVariant.product = cart.lines.find(
      (line: any) => line.merchandise.sku === activeVariant.sku,
    )?.merchandise.product
  }
  return {
    id: activeVariant.product?.id || activeVariant.product_id,
    title: activeVariant.product?.title || activeVariant.product_title,
    product_id: activeVariant.product?.id || activeVariant.product_id,
    handle: activeVariant.product?.handle,
    tags: activeVariant.product?.tags,
    product_type:
      activeVariant.product_type || activeVariant.product?.productType,
    featured_image: {
      url:
        activeVariant.product?.featuredImage?.url ||
        activeVariant.product?.shopifyImageUrl ||
        activeVariant.featured_image?.url,
    },
    vendor: activeVariant.product?.vendor,
    price: activeVariant.sku?.startsWith('CB-')
      ? activeVariant.price
      : String(activeVariant.price * 100),
    variant_id: activeVariant.id,
    sku: activeVariant.sku,
    is_system_item: activeVariant.sku?.includes('_SY_'),
  }
}

export const transformToChordItem = (
  activeVariant: any,
  product: any = null,
  addons: any = null,
  meals: any = null,
) => {
  return {
    product: {
      details: {
        title:
          product?.title ||
          product?.details?.title ||
          activeVariant.product?.title ||
          activeVariant.product_title,
        vendor: activeVariant.product?.vendor,
        productType:
          activeVariant.product?.productType || activeVariant.product_type,
        shopifyImageUrl: activeVariant.product?.shopifyImageUrl,
        id: activeVariant.product?.id || activeVariant.id,
      },
      variants: activeVariant.product
        ? activeVariant.product?.variants.map((variant: any) => ({
            id: variant.id,
            title: variant.title,
            price: variant.price,
            compareAtPrice: variant.compareAtPrice,
            sku: variant.sku,
            selected: variant.id === activeVariant.id,
          }))
        : [
            {
              id: activeVariant.id,
              title: activeVariant.title,
              price: activeVariant.price,
              compareAtPrice: activeVariant.compareAtPrice,
              sku: activeVariant.sku,
              selected: true,
            },
          ],
      addons: addons
        ? Object.values(addons).flatMap(({ addons }: any) =>
            addons.map(({ addon, quantity }: any) => ({
              title: addon.label,
              variant: addon.variant,
              quantity: quantity,
            })),
          )
        : null,
      meals: meals ? meals.join(', ') : '',
    },
  }
}
